import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { ArrowLeft, Ban, CheckCircle2, Gift, Globe, MapPin, PauseCircle, Phone, UserCog } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, ErrorState, Field, LoadingState } from '@pixlide/ui';
import { ApiError } from '@/api/client';
import {
  useActivateTenant,
  useCancelTenant,
  useDisableTenantUser,
  useEnableTenantUser,
  useGrantComplimentarySubscription,
  useSuspendTenant,
  useTenantDetail,
  useTenantUsers,
} from '@/api/queries/platform-admin';
import type { components } from '@/api/generated/schema';
import {
  ROLE_LABELS,
  USER_STATUS_LABELS,
  USER_STATUS_TONES,
} from '@/features/users/user-labels';

type TenantStatus = components['schemas']['PixlideDomainIdentityEnumsTenantStatus'];

const STATUS_LABELS: Record<TenantStatus, string> = {
  0: 'Essai',
  1: 'Actif',
  2: 'Suspendu',
  3: 'Résilié',
};

const STATUS_TONES: Record<TenantStatus, 'neutral' | 'success' | 'warning' | 'danger'> = {
  0: 'neutral',
  1: 'success',
  2: 'warning',
  3: 'danger',
};

type PlanCode = components['schemas']['PixlideDomainBillingEnumsPlanCode'];

/** `PlanCode` arrive en entier depuis l'API : sans libellé, la fiche affichait « 0 ». */
const PLAN_LABELS: Record<PlanCode, string> = {
  0: 'Starter',
  1: 'Essentiel',
  2: 'Pro',
  3: 'Max',
};

type PendingAction = 'suspend' | 'activate' | 'cancel' | null;

/**
 * Détail d'un tenant, avec les trois actions de cycle de vie.
 *
 * Chaque action passe par une CONFIRMATION EN DEUX TEMPS plutôt qu'une boîte de dialogue
 * modale : `@pixlide/ui` n'expose pas de composant modal (le design system de `frontend/`
 * n'en a jamais eu besoin), et une résiliation de tenant est une action rare et lourde de
 * conséquences — le bouton bascule sur un état « Confirmer » qui doit être cliqué une
 * seconde fois, ce qui suffit à empêcher un clic accidentel sans construire un composant
 * dédié pour un unique écran.
 */
export function TenantDetailPage() {
  const { tenantId } = useParams({ from: '/admin/tenants/$tenantId' });
  const tenant = useTenantDetail(tenantId);
  const suspend = useSuspendTenant();
  const activate = useActivateTenant();
  const cancel = useCancelTenant();

  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [suspendReason, setSuspendReason] = useState('');

  if (tenant.isPending) {
    return <LoadingState label="Chargement du tenant…" />;
  }

  if (tenant.isError || !tenant.data) {
    return (
      <ErrorState message="Impossible de charger ce tenant." onRetry={() => void tenant.refetch()} />
    );
  }

  const data = tenant.data;
  const isMutating = suspend.isPending || activate.isPending || cancel.isPending;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <Link to="/tenants" className="flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-900">
        <ArrowLeft aria-hidden className="size-4" />
        Retour aux tenants
      </Link>

      <Card className="flex flex-wrap items-center gap-4 p-6">
        <span
          aria-hidden
          className="grid size-14 shrink-0 place-items-center rounded-2xl bg-brand-100 text-lg font-semibold text-brand-700"
        >
          {data.companyName.slice(0, 2).toUpperCase()}
        </span>

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-xl font-semibold tracking-tight">{data.companyName}</h1>
            <Badge tone={STATUS_TONES[data.status]}>{STATUS_LABELS[data.status]}</Badge>
          </div>
          <p className="mt-0.5 text-sm text-ink-500">{data.slug}</p>
        </div>
      </Card>

      {/* Contenu principal + colonne latérale « Abonnement », comme sur la référence Figma
          (heymotion) — la table des utilisateurs reste pleine largeur en dessous, elle ne
          gagne rien à être compressée dans une colonne étroite. */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex flex-col gap-6 lg:col-span-2">
          <Card className="grid grid-cols-2 gap-4 p-6 text-sm">
            <div>
              <p className="text-ink-500">Utilisateurs</p>
              <p className="text-lg font-semibold">{data.userCount}</p>
            </div>

            <div>
              <p className="text-ink-500">Pays</p>
              <p className="text-lg font-semibold">{data.countryCode}</p>
            </div>

            <div>
              <p className="text-ink-500">Créé le</p>
              <p className="font-medium">{new Date(data.createdAt).toLocaleDateString('fr-FR')}</p>
            </div>

            {data.city ? (
              <div>
                <p className="flex items-center gap-1.5 text-ink-500">
                  <MapPin aria-hidden className="size-3.5" />
                  Ville
                </p>
                <p className="font-medium">{data.city}</p>
              </div>
            ) : null}

            {data.phoneNumber ? (
              <div>
                <p className="flex items-center gap-1.5 text-ink-500">
                  <Phone aria-hidden className="size-3.5" />
                  Téléphone
                </p>
                <p className="font-medium">{data.phoneNumber}</p>
              </div>
            ) : null}

            {data.websiteUrl ? (
              <div>
                <p className="flex items-center gap-1.5 text-ink-500">
                  <Globe aria-hidden className="size-3.5" />
                  Site web
                </p>
                <a
                  href={data.websiteUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-medium text-brand-700 hover:underline"
                >
                  {data.websiteUrl}
                </a>
              </div>
            ) : null}

            {data.vatNumber ? (
              <div>
                <p className="text-ink-500">N° TVA</p>
                <p className="font-medium">{data.vatNumber}</p>
              </div>
            ) : null}

            {data.suspensionReason ? (
              <div className="col-span-2 border-t border-ink-100/70 pt-4">
                <p className="text-ink-500">Motif de suspension</p>
                <p className="font-medium">{data.suspensionReason}</p>
              </div>
            ) : null}
          </Card>

          <Card className="flex flex-col gap-4 p-6">
            <h2 className="text-sm font-semibold text-ink-700">Actions</h2>

            {pendingAction === 'suspend' ? (
              <div className="flex flex-col gap-3">
                <Field
                  label="Motif de la suspension"
                  value={suspendReason}
                  onChange={(event) => setSuspendReason(event.target.value)}
                  required
                />
                <div className="flex gap-2">
                  <Button
                    variant="danger"
                    isLoading={suspend.isPending}
                    disabled={suspendReason.trim() === ''}
                    onClick={async () => {
                      await suspend.mutateAsync({ tenantId, reason: suspendReason.trim() });
                      setPendingAction(null);
                      setSuspendReason('');
                    }}
                  >
                    Confirmer la suspension
                  </Button>
                  <Button variant="ghost" onClick={() => setPendingAction(null)}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="secondary"
                  disabled={isMutating || data.status === 2}
                  onClick={() => setPendingAction('suspend')}
                >
                  <PauseCircle aria-hidden className="size-4" />
                  Suspendre
                </Button>

                <Button
                  variant="secondary"
                  disabled={isMutating || data.status === 1}
                  isLoading={activate.isPending}
                  onClick={() => activate.mutate({ tenantId })}
                >
                  <CheckCircle2 aria-hidden className="size-4" />
                  Réactiver
                </Button>

                <ConfirmCancelButton
                  disabled={isMutating || data.status === 3}
                  isPending={cancel.isPending}
                  onConfirm={() => cancel.mutate({ tenantId })}
                />
              </div>
            )}
          </Card>
        </div>

        <Card className="flex h-fit flex-col gap-3 p-6">
          <h2 className="text-sm font-semibold text-ink-700">Abonnement</h2>

          {data.activeSubscription ? (
            <div className="flex flex-col gap-3 text-sm">
              <div>
                <p className="text-ink-500">Offre</p>
                <p className="text-lg font-semibold">{PLAN_LABELS[data.activeSubscription.planCode]}</p>
              </div>

              <div>
                <p className="text-ink-500">Écrans autorisés</p>
                <p className="font-medium">{data.activeSubscription.screenAllowance}</p>
              </div>

              <div>
                <p className="text-ink-500">Échéance</p>
                <p className="font-medium">
                  {new Date(data.activeSubscription.currentPeriodEnd).toLocaleDateString('fr-FR')}
                </p>
              </div>

              {data.activeSubscription.isComplimentary ? (
                <Badge tone="brand">Offert</Badge>
              ) : data.activeSubscription.cancelAtPeriodEnd ? (
                <Badge tone="warning">Résiliation programmée</Badge>
              ) : (
                <Badge tone="success">Renouvellement automatique</Badge>
              )}
            </div>
          ) : (
            <p className="text-sm text-ink-500">Aucun abonnement actif pour ce tenant.</p>
          )}

          {/* Uniquement hors production, et jamais par-dessus un abonnement payant : c'est
              l'API qui en décide (canGrantComplimentarySubscription). */}
          {data.canGrantComplimentarySubscription ? <GrantComplimentaryButton tenantId={tenantId} /> : null}
        </Card>
      </div>

      <TenantUsersSection tenantId={tenantId} />
    </div>
  );
}

/** Table des utilisateurs de ce tenant, avec action Désactiver/Réactiver par ligne. */
function TenantUsersSection({ tenantId }: { tenantId: string }) {
  const users = useTenantUsers(tenantId);

  return (
    <Card className="flex flex-col gap-4 p-6">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-700">
        <UserCog aria-hidden className="size-4" />
        Utilisateurs
      </h2>

      {users.isPending ? <LoadingState label="Chargement des utilisateurs…" /> : null}

      {users.isError ? (
        <ErrorState
          message="Impossible de charger les utilisateurs de ce tenant."
          onRetry={() => void users.refetch()}
        />
      ) : null}

      {users.data && users.data.items.length === 0 ? (
        <p className="text-sm text-ink-500">Ce tenant n'a aucun utilisateur.</p>
      ) : null}

      {users.data && users.data.items.length > 0 ? (
        <div className="-mx-6 overflow-x-auto">
          <table className="w-full text-sm">
            <caption className="sr-only">Liste des utilisateurs de ce tenant</caption>

            <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
              <tr>
                <th scope="col" className="px-6 py-3">Nom</th>
                <th scope="col" className="px-6 py-3">Email</th>
                <th scope="col" className="px-6 py-3">Rôle</th>
                <th scope="col" className="px-6 py-3">Statut</th>
                <th scope="col" className="px-6 py-3">
                  <span className="sr-only">Action</span>
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-ink-100/70">
              {users.data.items.map((user) => (
                <TenantUserRow key={user.id} tenantId={tenantId} user={user} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Card>
  );
}

type TenantUserItem = NonNullable<ReturnType<typeof useTenantUsers>['data']>['items'][number];

/** Une ligne de la table, avec sa propre confirmation en deux temps pour Désactiver/Réactiver. */
function TenantUserRow({ tenantId, user }: { tenantId: string; user: TenantUserItem }) {
  const disableUser = useDisableTenantUser();
  const enableUser = useEnableTenantUser();
  const [confirming, setConfirming] = useState(false);

  const isDisabled = user.status === 2;
  const isMutating = disableUser.isPending || enableUser.isPending;

  return (
    <tr>
      <th scope="row" className="px-6 py-4 text-left font-medium">
        {user.firstName} {user.lastName}
      </th>

      <td className="px-6 py-4 text-ink-500">{user.email}</td>
      <td className="px-6 py-4 text-ink-500">{ROLE_LABELS[user.role]}</td>

      <td className="px-6 py-4">
        <Badge tone={USER_STATUS_TONES[user.status]}>{USER_STATUS_LABELS[user.status]}</Badge>
      </td>

      <td className="px-6 py-4 text-right">
        {confirming ? (
          <div className="flex justify-end gap-2">
            <Button
              variant="danger"
              size="sm"
              isLoading={isMutating}
              onClick={() => {
                if (isDisabled) {
                  enableUser.mutate({ tenantId, userId: user.id });
                } else {
                  disableUser.mutate({ tenantId, userId: user.id });
                }
                setConfirming(false);
              }}
            >
              Confirmer
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              Annuler
            </Button>
          </div>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            disabled={isMutating}
            onClick={() => setConfirming(true)}
          >
            {isDisabled ? 'Réactiver' : 'Désactiver'}
          </Button>
        )}
      </td>
    </tr>
  );
}

/** Messages indexés par le CODE stable de l'API, jamais par son message. */
const GRANT_ERRORS: Record<string, string> = {
  'subscription.complimentary_disabled': "L'offre d'abonnements est désactivée sur cet environnement.",
  'payment.provider_error': "Stripe n'a pas pu résilier l'abonnement payant : rien n'a été modifié, réessayez.",
  'payment.not_configured': "Stripe n'est pas configuré sur cet environnement : l'abonnement payant ne peut pas être résilié.",
  'tenant.cancelled': 'Ce compte est résilié.',
  'plan.not_found': "La formule Max est introuvable : vérifiez les offres dans le back-office.",
};

function describeGrantError(error: unknown): string {
  if (error instanceof ApiError) {
    return GRANT_ERRORS[error.code] ?? error.message;
  }

  return "L'offre n'a pas pu être appliquée.";
}

/**
 * « Offrir 500 écrans », à confirmation en deux clics comme les autres actions de la fiche :
 * offrir le service est un geste commercial, pas un clic à faire par mégarde.
 */
function GrantComplimentaryButton({ tenantId }: { tenantId: string }) {
  const grant = useGrantComplimentarySubscription();
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="flex flex-col gap-2 border-t border-ink-100/70 pt-3">
      {confirming ? (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            isLoading={grant.isPending}
            onClick={() => {
              grant.mutate({ tenantId }, { onSettled: () => setConfirming(false) });
            }}
          >
            Confirmer l'offre
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
            Annuler
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
          <Gift aria-hidden className="size-4" />
          Offrir 500 écrans
        </Button>
      )}

      <p className="text-xs text-ink-500">
        Abonnement Max gratuit, sans paiement. Un abonnement payant en cours est résilié chez
        Stripe. Disponible hors production uniquement.
      </p>

      {grant.isError ? (
        <p role="alert" className="text-xs font-medium text-danger-500">
          {describeGrantError(grant.error)}
        </p>
      ) : null}
    </div>
  );
}

/** Bouton « Résilier » à confirmation en deux clics, isolé pour garder son propre état local. */
function ConfirmCancelButton({
  disabled,
  isPending,
  onConfirm,
}: {
  disabled: boolean;
  isPending: boolean;
  onConfirm: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex gap-2">
        <Button
          variant="danger"
          isLoading={isPending}
          onClick={() => {
            onConfirm();
            setConfirming(false);
          }}
        >
          Confirmer la résiliation
        </Button>
        <Button variant="ghost" onClick={() => setConfirming(false)}>
          Annuler
        </Button>
      </div>
    );
  }

  return (
    <Button variant="danger" disabled={disabled} onClick={() => setConfirming(true)}>
      <Ban aria-hidden className="size-4" />
      Résilier
    </Button>
  );
}
